/**
 * Shared SignalR console diagnostics for troubleshooting connection issues.
 */

export type SignalRHubName = 'agentExecution' | 'metrics' | 'transcription';

/**
 * Normalizes an unknown error into a plain object safe for console logging.
 */
export function formatSignalRError(error: unknown): Record<string, unknown> {
  if (error == null) {
    return { message: 'Unknown error (null/undefined)' };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error instanceof Error) {
    const anyErr = error as Error & {
      errorType?: string;
      statusCode?: number;
      transport?: string;
      source?: string;
    };

    return {
      name: anyErr.name,
      message: anyErr.message,
      stack: anyErr.stack,
      ...(anyErr.errorType ? { errorType: anyErr.errorType } : {}),
      ...(anyErr.statusCode != null ? { statusCode: anyErr.statusCode } : {}),
      ...(anyErr.transport ? { transport: anyErr.transport } : {}),
      ...(anyErr.source ? { source: anyErr.source } : {}),
    };
  }

  if (typeof error === 'object') {
    try {
      return { ...error };
    } catch {
      return { message: String(error) };
    }
  }

  return { message: String(error) };
}

/**
 * Logs an informational SignalR lifecycle event.
 */
export function logSignalR(
  hub: SignalRHubName,
  event: string,
  details?: Record<string, unknown>,
): void {
  console.info(`[SignalR:${hub}] ${event}`, details ?? {});
}

/**
 * Logs a SignalR warning (e.g. reconnecting).
 */
export function logSignalRWarn(
  hub: SignalRHubName,
  event: string,
  error?: unknown,
  details?: Record<string, unknown>,
): void {
  console.warn(`[SignalR:${hub}] ${event}`, {
    ...(error != null ? { error: formatSignalRError(error) } : {}),
    ...details,
  });
}

/**
 * Logs a SignalR error with full detail for troubleshooting.
 */
export function logSignalRError(
  hub: SignalRHubName,
  event: string,
  error: unknown,
  details?: Record<string, unknown>,
): void {
  console.error(`[SignalR:${hub}] ${event}`, {
    error: formatSignalRError(error),
    ...details,
  });
}

/**
 * Builds a short user-facing message from a SignalR error.
 */
export function getSignalRErrorMessage(error: unknown, fallback = 'Real-time connection failed'): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}
